import { ptBR } from '@/i18n/pt-BR';
import { roleLabel } from '@/lib/roles';
import type { AmbienteAdmin, PapelAdmin, UsuarioAdmin } from '@/lib/plataforma_admin';
import {
  Blueprint,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

interface UsuariosTableProps {
  usuarios: UsuarioAdmin[] | null;
  erro: string | null;
  onEditar: (usuarioId: string) => void;
}

/**
 * Papéis oferecidos por ambiente — o mesmo recorte do servidor. // ux-neutrality:allow doc-comment
 *
 * Oferecer só o que cabe evita a recusa boba; mas quem decide continua sendo o // ux-neutrality:allow doc-comment
 * servidor, e uma tentativa fora do ambiente volta com a frase dele. // ux-neutrality:allow doc-comment
 */
export const PAPEIS_POR_AMBIENTE: Record<AmbienteAdmin, readonly PapelAdmin[]> = {
  banco: ['bank_manager', 'bank_user'],
  plataforma: ['owner', 'member', 'revisor', 'certificador'],
  escritorio: ['owner', 'member', 'revisor'],
};

function _rotuloAmbiente(ambiente: AmbienteAdmin): string {
  if (ambiente === 'plataforma') return ptBR.admin.ambientePlataforma;
  if (ambiente === 'banco') return ptBR.admin.ambienteBanco;
  return ptBR.admin.ambienteEscritorio;
}

/**
 * Lista cross-tenant de pessoas (E53.F6). Desde a E175.F1 cada linha só abre o // ux-neutrality:allow doc-comment
 * painel da pessoa: papel, acesso, dados, senha e escritório vivem lá. // ux-neutrality:allow doc-comment
 */
export function UsuariosTable({ usuarios, erro, onEditar }: UsuariosTableProps) {
  if (erro !== null) {
    return (
      <p data-testid="usuarios-erro" className="text-body-md text-danger-700">
        {erro}
      </p>
    );
  }
  if (usuarios === null) {
    return (
      <p data-testid="usuarios-carregando" className="text-body-md text-fg-muted">
        {ptBR.admin.usuariosCarregando}
      </p>
    );
  }
  if (usuarios.length === 0) {
    return (
      <p data-testid="usuarios-vazio" className="text-body-md text-fg-muted">
        {ptBR.admin.usuariosVazio}
      </p>
    );
  }

  return (
    <Blueprint className="min-w-0">
      <div className="min-w-0 overflow-x-auto [contain:layout]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{ptBR.admin.usuarioColPessoa}</TableHead>
              <TableHead>{ptBR.admin.usuarioColAmbiente}</TableHead>
              <TableHead>{ptBR.admin.usuarioColPapel}</TableHead>
              <TableHead>{ptBR.admin.usuarioColEstado}</TableHead>
              <TableHead>
                <span className="sr-only">{ptBR.admin.usuarioColAcoes}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usuarios.map((usuario) => {
              const vinculo = usuario.banco_nome ?? usuario.escritorio_nome;
              return (
                <TableRow key={usuario.id} className="align-top" data-testid="usuario-linha">
                  <TableCell>
                    <span className="block text-fg">{usuario.display_name ?? usuario.email}</span>
                    {usuario.display_name !== null ? (
                      <span className="block font-mono text-caption text-fg-muted">
                        {usuario.email}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-fg-muted">
                    <span className="block">{_rotuloAmbiente(usuario.ambiente)}</span>
                    <span className="block text-caption">{vinculo ?? ptBR.admin.valorAusente}</span>
                  </TableCell>
                  <TableCell className="text-fg-muted" data-testid="usuario-papel">
                    {roleLabel(usuario.role)}
                  </TableCell>
                  <TableCell data-testid="usuario-estado">
                    <span className={usuario.ativo ? 'text-accent-700' : 'text-fg-muted'}>
                      {usuario.ativo ? ptBR.admin.estadoAtivo : ptBR.admin.estadoInativo}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={() => onEditar(usuario.id)}
                      data-testid={`pessoa-editar-${usuario.id}`}
                    >
                      {ptBR.admin.pessoaEditar}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Blueprint>
  );
}
